import {
  AbpApplicationConfigurationService,
  AbpApplicationLocalizationService,
  CHECK_AUTHENTICATION_STATE_FN_KEY,
  ConfigStateService,
  CoreModule,
  PermissionService,
  ReplaceableComponentsService,
} from '@abp/ng.core';
import { FeatureManagementModule } from '@abp/ng.feature-management';
import { AbpOAuthModule } from '@abp/ng.oauth';
import { SettingManagementConfigModule } from '@abp/ng.setting-management/config';
import { InternetConnectionStatusComponent, ThemeSharedModule } from '@abp/ng.theme.shared';
import { CommonModule } from '@angular/common';
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { OAuthService } from 'angular-oauth2-oidc';
import { CommercialUiConfigModule } from '@volo/abp.commercial.ng.ui/config';
import { AccountAdminConfigModule } from '@volo/abp.ng.account/admin/config';
import { AccountPublicConfigModule } from '@volo/abp.ng.account/public/config';
import { AuditLoggingConfigModule } from '@volo/abp.ng.audit-logging/config';
import { GdprConfigModule } from '@volo/abp.ng.gdpr/config';
import { IdentityConfigModule } from '@volo/abp.ng.identity/config';
import { LanguageManagementConfigModule } from '@volo/abp.ng.language-management/config';
import { registerLocale } from '@volo/abp.ng.language-management/locale';
import { OpeniddictproConfigModule } from '@volo/abp.ng.openiddictpro/config';
import { SaasConfigModule } from '@volo/abp.ng.saas/config';
import { TextTemplateManagementConfigModule } from '@volo/abp.ng.text-template-management/config';
import { LpxResponsiveModule } from '@volo/ngx-lepton-x.core';
import { eThemeLeptonXComponents, ThemeLeptonXModule } from '@volosoft/abp.ng.theme.lepton-x';
import { SideMenuLayoutModule } from '@volosoft/abp.ng.theme.lepton-x/layouts';
import { LpxSideMenuLayoutModule } from '@volosoft/ngx-lepton-x/layouts';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MobileModule } from './components/menu/mobile.module';
import { CustomBreadcrumbComponent } from './custom-breadcrumb/custom-breadcrumb.component';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { AppPermissionConsts } from './shared/consts/app-permission.const';
import { LocalDbModule } from './shared/local-db/local-db.module';
import { HealthCheckServiceProxy } from './shared/services-proxies/HealthCkeckServiceProxy';
import { CustomApplicationConfigurationService } from './shared/services/custom-application-configuration.service';
import { CustomApplicationLocalizationService } from './shared/services/custom-application-localization.service';
import { ErrorHandlerService } from './shared/services/error-handler-service';
import { HealthCheckService } from './shared/services/health-check-service';
import { SharedModule } from './shared/shared.module';
import { AppVersionService } from "./shared/services/app-version-service";
import { clearOAuthStorage } from '@abp/ng.oauth';

const safeCheckAccessToken = (injector: Injector) => {
  const configState = injector.get<ConfigStateService>(ConfigStateService);
  const oAuth = injector.get<OAuthService>(OAuthService);

  if (oAuth.hasValidAccessToken() && !configState.getDeep('currentUser.id')) {
    clearOAuthStorage(injector);
  }
};
export function replaceBreadcrumb(
  replaceableComponents: ReplaceableComponentsService,
  permissionService: PermissionService
) {
  return () => {
    return new Promise<void>(resolve => {
      permissionService
        .getGrantedPolicy$(AppPermissionConsts.Approvals.Default)
        .subscribe(hasPermission => {
          if (hasPermission) {
            replaceableComponents.add({
              component: CustomBreadcrumbComponent,
              key: eThemeLeptonXComponents.Breadcrumb,
            });
          }
          resolve();
        });
    });
  };
}

@NgModule({
  declarations: [AppComponent, CustomBreadcrumbComponent],
  imports: [
    LocalDbModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    MobileModule,
    SharedModule,
    CommonModule,
    CoreModule.forRoot({
      environment,
      registerLocaleFn: registerLocale(),
    }),
    AbpOAuthModule.forRoot(),
    ThemeSharedModule.forRoot(),
    AccountAdminConfigModule.forRoot(),
    AccountPublicConfigModule.forRoot(),
    IdentityConfigModule.forRoot(),
    LanguageManagementConfigModule.forRoot(),
    SaasConfigModule.forRoot(),
    AuditLoggingConfigModule.forRoot(),
    OpeniddictproConfigModule.forRoot(),
    TextTemplateManagementConfigModule.forRoot(),
    SettingManagementConfigModule.forRoot(),

    CommercialUiConfigModule.forRoot(),
    FeatureManagementModule.forRoot(),
    GdprConfigModule.forRoot({
      privacyPolicyUrl: 'gdpr-cookie-consent/privacy',
      cookiePolicyUrl: 'gdpr-cookie-consent/cookie',
    }),
    InternetConnectionStatusComponent,
    ThemeLeptonXModule.forRoot({
      defaultTheme: 'light',
    }),
    SideMenuLayoutModule.forRoot(),
    LpxSideMenuLayoutModule,
    LpxResponsiveModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: replaceBreadcrumb,
      deps: [ReplaceableComponentsService, PermissionService],
      multi: true,
    },
    {
      provide: CHECK_AUTHENTICATION_STATE_FN_KEY,
      useValue: safeCheckAccessToken,
    },
    APP_ROUTE_PROVIDER,
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    AppVersionService,
    HealthCheckServiceProxy,
    HealthCheckService,
    {
      provide: AbpApplicationConfigurationService,
      useClass: CustomApplicationConfigurationService,
    },
    {
      provide: AbpApplicationLocalizationService,
      useClass: CustomApplicationLocalizationService,
    },
  ],
  bootstrap: [AppComponent],
  exports: [CustomBreadcrumbComponent],
})
export class AppModule {}
